import React, { useState, useEffect } from 'react';
import { useGlobalStorageContext } from '../contexts/GlobalStorageContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { buildPrayerTimesForDate, listPrayerTimes, prayerEnumToName, formatTime, msToHhMm } from '../services/prayerService';
import { getRandomDua } from '../services/duaService';
import { SALAT_PDF_URL } from '../config/constantConfig';
import PrayerTimes from 'adhan/lib/types/PrayerTimes';
import './PrayerPage.css';

export const PrayerPage: React.FC = () => {
  const { t } = useLanguage();
  const { isDarkMode, colors } = useTheme();
  const { manualLocation, calcSettings, hijriAdjustment } = useGlobalStorageContext();
  const [ptToday, setPtToday] = useState<PrayerTimes | null>(null);
  const [ptTomorrow, setPtTomorrow] = useState<PrayerTimes | null>(null);
  const [now, setNow] = useState(new Date());
  const [dayOffset, setDayOffset] = useState(0);
  const [currentDua, setCurrentDua] = useState(() => getRandomDua());
  const [showFullDua, setShowFullDua] = useState(false);

  useEffect(() => {
    if (!manualLocation || !calcSettings) return;
    
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + dayOffset);
    const today = buildPrayerTimesForDate('', targetDate, manualLocation, calcSettings);
    setPtToday(today);
    
    const tomorrow = new Date(targetDate);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowPrayer = buildPrayerTimesForDate('', tomorrow, manualLocation, calcSettings);
    setPtTomorrow(tomorrowPrayer);
  }, [manualLocation, calcSettings, dayOffset]);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  if (!ptToday || !ptTomorrow) {
    return <div className="loading">{t.prayer.loading}</div>;
  }

  const nextPrayerEnum = ptToday.nextPrayer();
  const nextName = prayerEnumToName(nextPrayerEnum);
  const nextAt = ptToday.timeForPrayer(nextPrayerEnum) ?? ptTomorrow.fajr;
  const msToNext = nextAt.getTime() - now.getTime();
  const todayList = listPrayerTimes(ptToday);
  const isToday = dayOffset === 0;
  
  const displayDate = new Date();
  displayDate.setDate(displayDate.getDate() + dayOffset);

  const miniStats = [
    { title: t.more.fajrToMaghrib, value: msToHhMm(ptToday.maghrib.getTime() - ptToday.fajr.getTime()) },
    { title: t.more.sunriseToSunset, value: msToHhMm(ptToday.maghrib.getTime() - ptToday.sunrise.getTime()) },
  ];

  return (
    <div className="prayer-page" style={{ backgroundColor: colors.background, color: colors.text }}>
      <div className="date-nav">
        <button onClick={() => setDayOffset(dayOffset - 1)} className="nav-btn">‹</button>
        <span className="date-text">{isToday ? t.prayer.today : displayDate.toLocaleDateString()}</span>
        <button onClick={() => setDayOffset(0)} className="nav-btn">●</button>
        <button onClick={() => setDayOffset(dayOffset + 1)} className="nav-btn">›</button>
      </div>

      {isToday && (
        <div className="active-card" style={{ backgroundColor: isDarkMode ? '#1f2937' : colors.tint }}>
          <div className="active-card-content">
            <span className="next-prayer-name">{t.prayer.nextPrayerIn} {nextName} {t.prayer.in}</span>
            <span className="countdown">{msToHhMm(msToNext)}</span>
          </div>
        </div>
      )}

      <div className="table-container" style={{ borderColor: colors.border }}>
        {todayList.map((p) => {
          const isNext = isToday && p.name === nextName;
          return (
            <div
              key={p.name}
              className="row"
              style={{
                backgroundColor: isNext ? (isDarkMode ? '#422006' : '#fef3c7') : colors.cardBackground,
                borderBottomColor: colors.border,
              }}
            >
              <span className="row-left" style={{ color: colors.text }}>{p.name}</span>
              <span className="row-right" style={{ color: colors.muted }}>{formatTime(p.at)}</span>
            </div>
          );
        })}
      </div>

      <div className="mini-stats-card" style={{ backgroundColor: colors.cardBackground, borderColor: colors.border }}>
        {miniStats.map((stat, i) => (
          <div key={i} className="mini-stat-item">
            <span className="mini-stat-title" style={{ color: colors.muted }}>{stat.title}</span>
            <span className="mini-stat-value" style={{ color: colors.text }}>{stat.value}</span>
          </div>
        ))}
      </div>

      <div className="dua-card" style={{ backgroundColor: colors.cardBackground, borderColor: colors.border }}>
        <div className="dua-header">
          <span className="dua-title" style={{ color: colors.text }}>{t.more.duas}</span>
          <button onClick={() => setCurrentDua(getRandomDua())} className="refresh-btn" style={{ color: colors.tint }}>↻</button>
        </div>
        <div className="dua-title-text" style={{ color: colors.muted }}>{currentDua.title}</div>
        <div className="dua-arabic" style={{ color: colors.text }}>
          {showFullDua ? currentDua.arabicText : currentDua.arabicText.slice(0, 150) + (currentDua.arabicText.length > 150 ? '...' : '')}
        </div>
        {currentDua.arabicText.length > 150 && (
          <button onClick={() => setShowFullDua(!showFullDua)} className="more-btn" style={{ color: colors.tint }}>
            {showFullDua ? t.more.less : t.more.more}
          </button>
        )}
      </div>

      <div className="app-download-section" style={{ backgroundColor: colors.cardBackground, borderColor: colors.border, padding: '24px', borderRadius: '16px', marginTop: '16px', textAlign: 'center' }}>
        <div style={{ fontSize: '14px', fontWeight: 'bold', color: colors.text, marginBottom: '16px' }}>{t.more.downloadApp}</div>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href="https://play.google.com/store/apps/details?id=com.anamzooya.rukousajat" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block' }}>
            <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Get it on Google Play" style={{ height: '40px' }} />
          </a>
          <a href="https://apps.apple.com/app/id1334325381" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block' }}>
            <img src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" alt="Download on the App Store" style={{ height: '40px' }} />
          </a>
        </div>
      </div>
    </div>
  );
};
